import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ArrowRight, CalendarDays, Clock, MapPin, Star } from 'lucide-react';
import { API } from '../lib/api';

const hasVisibleContent = (item) => Boolean(
  item?.headline ||
  item?.sub_headline ||
  item?.description ||
  item?.event_location ||
  item?.event_date ||
  item?.event_time ||
  item?.event_end_time ||
  item?.ticket_url ||
  item?.image_url
);

const formatEventDate = (value) => {
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
};

const PremiumEventsSection = () => {
  const [items, setItems] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/premium-events`)
      .then((response) => {
        if (!mounted) return;
        setItems((response.data || []).filter(hasVisibleContent));
      })
      .catch((error) => console.error('Error fetching premium events:', error));
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (items.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % items.length);
    }, 3000);
    return () => window.clearInterval(timer);
  }, [items.length]);

  const activeItem = useMemo(() => items[activeIndex] || items[0], [items, activeIndex]);

  if (!activeItem) return null;

  return (
    <section className="relative min-h-screen bg-[#0A0A0A] border-y border-zinc-900 overflow-hidden flex items-center" data-testid="premium-events-section">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(244,63,94,0.22),transparent_32%),radial-gradient(circle_at_80%_10%,rgba(168,85,247,0.18),transparent_30%),linear-gradient(135deg,rgba(10,10,10,0.96),rgba(10,10,10,0.82))] pointer-events-none" />
      <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid min-h-[calc(100vh-8rem)] grid-cols-1 lg:grid-cols-[1.12fr_0.88fr] gap-10 items-center rounded-3xl border border-zinc-800 bg-zinc-950/80 p-5 md:p-8 lg:p-10 shadow-2xl shadow-purple-950/20">
          {activeItem.image_url && (
            <div className="relative flex min-h-[42vh] lg:min-h-[calc(100vh-14rem)] items-center justify-center overflow-hidden rounded-2xl bg-black/50 border border-white/10 p-3 md:p-5">
              <img src={activeItem.image_url} alt={activeItem.headline || 'Premium event'} className="max-h-full max-w-full object-contain" />
            </div>
          )}

          <div className={activeItem.image_url ? 'space-y-5 lg:pr-2' : 'space-y-5 lg:col-span-2'}>
            <div className="inline-flex items-center gap-2 rounded-full border border-rose-500/30 bg-rose-500/10 px-4 py-2 text-sm font-semibold uppercase tracking-[0.25em] text-rose-300">
              <Star size={16} /> Premium Event
            </div>
            {activeItem.headline && <h2 className="font-outfit text-3xl md:text-5xl font-bold text-white leading-tight">{activeItem.headline}</h2>}
            {activeItem.sub_headline && <p className="text-xl text-purple-200 font-medium">{activeItem.sub_headline}</p>}
            {(activeItem.event_date || activeItem.event_time || activeItem.event_end_time || activeItem.event_location) && (
              <div className="flex flex-wrap gap-3 text-sm md:text-base text-rose-100">
                {activeItem.event_date && <span className="inline-flex items-center gap-2 rounded-full bg-white/5 px-4 py-2"><CalendarDays size={16} />{formatEventDate(activeItem.event_date)}</span>}
                {(activeItem.event_time || activeItem.event_end_time) && <span className="inline-flex items-center gap-2 rounded-full bg-white/5 px-4 py-2"><Clock size={16} />{[activeItem.event_time, activeItem.event_end_time].filter(Boolean).join(' – ')}</span>}
                {activeItem.event_location && <span className="inline-flex items-center gap-2 rounded-full bg-white/5 px-4 py-2"><MapPin size={16} />{activeItem.event_location}</span>}
              </div>
            )}
            {activeItem.description && <p className="text-zinc-300 text-lg leading-relaxed max-w-3xl">{activeItem.description}</p>}
            {activeItem.ticket_url && (
              <a href={activeItem.ticket_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-rose-500 px-6 py-3 font-semibold text-white transition hover:bg-rose-600">
                Get Tickets <ArrowRight size={18} />
              </a>
            )}
            {items.length > 1 && (
              <div className="flex gap-2 pt-2" aria-label="Premium event slides">
                {items.map((item, index) => (
                  <button key={item.id} type="button" onClick={() => setActiveIndex(index)} className={`h-2.5 rounded-full transition-all ${index === activeIndex ? 'w-8 bg-rose-400' : 'w-2.5 bg-zinc-700'}`} aria-label={`Show premium event ${index + 1}`} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PremiumEventsSection;
